<div class="user-reports-page">
  <!-- ── Page header ──────────────────────────────────────── -->
  <div class="page-header">
    <div class="page-heading">
      <div class="page-title">
        {{ isManageFlags ? "Manage Flags" : "My Flags" }}
      </div>
      @if (!isManageFlags) {
        <div class="page-subtitle">
          Track and review the status of issues, data corrections, and feedback
          you've reported.
        </div>
      }
    </div>

    @if (reports.length > 0 || loading) {
      <div class="page-size">
        <app-page-size
          [pageSize]="pageSize"
          [selectId]="'user-reports-page-size'"
          (pageSizeChanged)="onPageSizeChange($event)"
        ></app-page-size>
      </div>
    }
  </div>

  <!-- ── Filter bar ───────────────────────────────────────── -->
  <div class="filters-bar" id="tour-flags-filters">
    <div class="filters-main">
      <div class="filters-header">
        <svg
          class="filters-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polygon
            points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"
          ></polygon>
        </svg>
        <span class="filters-title">Filters</span>
        @if (hasActiveFilters) {
          <span class="filters-badge">{{ activeFiltersCount }}</span>
        }
      </div>

      <div class="filters-divider"></div>

      <!-- Entity Type -->
      <div class="filter-field" [class.is-active]="entityTypeFilter !== null">
        <label class="filter-label" for="uf-entity-filter">Entity</label>
        <select
          id="uf-entity-filter"
          class="filter-select"
          [ngModel]="entityTypeFilter"
          (ngModelChange)="onEntityTypeFilterChange($event)"
        >
          @for (opt of entityTypeOptions; track opt.value) {
            <option [ngValue]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>

      <!-- Status -->
      <div class="filter-field" [class.is-active]="statusFilter !== null">
        <label class="filter-label" for="uf-status-filter">Status</label>
        <select
          id="uf-status-filter"
          class="filter-select"
          [ngModel]="statusFilter"
          (ngModelChange)="onStatusFilterChange($event)"
        >
          @for (opt of statusOptions; track opt.value) {
            <option [ngValue]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>

      <!-- Severity -->
      <div class="filter-field" [class.is-active]="severityFilter !== null">
        <label class="filter-label" for="uf-severity-filter">Severity</label>
        <select
          id="uf-severity-filter"
          class="filter-select"
          [ngModel]="severityFilter"
          (ngModelChange)="onSeverityFilterChange($event)"
        >
          @for (opt of severityOptions; track opt.value) {
            <option [ngValue]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>
    </div>

    @if (hasActiveFilters) {
      <button
        type="button"
        class="clear-filters-btn"
        (click)="resetFilters()"
        title="Reset all filters"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        <span>Reset Filters</span>
      </button>
    }
  </div>

  <!-- ── Table ─────────────────────────────────────────────── -->
  <app-table
    [columns]="columns"
    [data]="reports"
    [loading]="loading"
    [noResultsMessage]="
      isManageFlags
        ? 'No reports found for the selected filters.'
        : 'No flags found for the selected filters.'
    "
    [skeletonRows]="5"
  >
    <ng-template let-report let-index="index">
      <tr [id]="'tour-flag-row-' + index">
        <!-- Entity Type -->
        <td>
          <span class="entity-type">{{ report.entity_type }}</span>
        </td>

        <!-- Reported By (Manage Flags only) -->
        @if (isManageFlags) {
          <td>
            <div class="reporter-info">
              <span
                class="reporter-name"
                [title]="getReporterFullName(report.reporter)"
              >
                {{ getReporterFullName(report.reporter) || "—" }}
              </span>
              @if (report.reporter?.email) {
                <span class="reporter-email" [title]="report.reporter.email">
                  {{ report.reporter.email }}
                </span>
              }
            </div>
          </td>
        }

        <!-- Reason -->
        <td>
          <span class="reason-text" [title]="report.reason_label">
            {{ report.reason_label }}
          </span>
        </td>

        <!-- Severity -->
        <td>
          @if (report.severity_label) {
            <span
              class="severity-tag"
              [ngClass]="getSeverityClass(report.severity)"
            >
              {{ report.severity_label }}
            </span>
          } @else {
            <span class="empty-value">—</span>
          }
        </td>

        <!-- Status -->
        <td>
          @if (report.status) {
            <span
              class="report-tag"
              [id]="'tour-flag-status-' + index"
              [ngClass]="getStatusClass(report.status)"
            >
              {{ getStatusLabel(report.status) }}
            </span>
          } @else {
            <span class="empty-value">—</span>
          }
        </td>

        <!-- Created At -->
        <td>
          {{ report.created_at | date: "medium" }}
        </td>

        <!-- View -->
        <td>
          <div class="action-icons">
            <a
              href="javascript:void(0)"
              class="view-link"
              [id]="'tour-flag-view-btn-' + index"
              (click)="viewReport(report)"
            >
              View
            </a>
          </div>
        </td>
      </tr>
    </ng-template>
  </app-table>

  @if (reports.length > 0) {
    <div class="pagination-wrapper">
      <app-pagination
        [currentPage]="currentPage"
        [pageSize]="pageSize"
        [totalItems]="totalReports"
        (pageChanged)="onPageChange($event)"
      ></app-pagination>
    </div>
  }
</div>

<!-- ── Report Details secondary popup ───────────────────── -->
@if (showReportDetails && selectedReport) {
  <app-secondary-popup
    [title]="
      isManageFlags
        ? isEditMode
          ? 'Take Action'
          : 'Flag Details'
        : 'Flag Details'
    "
    [isActionButtonsAvailable]="isManageFlags"
    [primaryActionName]="isEditMode ? 'Save' : 'Take Action'"
    [secondaryActionName]="'Cancel'"
    [hideSecondaryButton]="!isEditMode"
    (popupClosed)="closeReportDetails($event)"
    (primaryAction)="onPopupPrimaryAction()"
    (secondaryAction)="cancelEdit()"
  >
    <app-user-flag-details
      [report]="selectedReport"
      [isManageFlags]="isManageFlags"
      [isEditMode]="isEditMode"
      [editForm]="editForm"
      [entityDetails]="entityDetails"
      (openFile)="openFileViewer($event)"
      (downloadFile)="downloadFile($event)"
      (openEntityFile)="openEntityFileViewer()"
      (downloadEntityFile)="downloadEntityFile()"
      (viewRecord)="showRecordDetails = true"
    ></app-user-flag-details>
  </app-secondary-popup>
}

@if (showFileViewer && selectedFile) {
  <app-file-viewer
    [fileUrl]="selectedFile.download_url || ''"
    [fileName]="selectedFile.file_name"
    [showViewer]="showFileViewer"
    (closeViewer)="onCloseViewer()"
    (downloadFile)="downloadSelectedFile()"
  ></app-file-viewer>
}

@if (showRecordDetails) {
  <app-record-details
    [unique_id]="recordUniqueId"
    [isActionButtonsAvailable]="false"
    (popupClosed)="showRecordDetails = false"
  ></app-record-details>
}
